import axios from "axios";
import { resolveOfferProducts } from "../../shared/resolve-offer-products-client.js";
import { api } from "../utils/interceptor";
import { formatProductPriceFields } from "../helper";
import { attachStorefrontTrackingToCartItems } from "../../shared/checkout-tracking.js";
import {
  resolveCartSectionsToRequest,
  syncStorefrontCartAfterAdd,
  syncStorefrontCartUi,
} from "../utils/sync-storefront-cart-after-add";
import { parseCartAddErrorFromResponse } from "../utils/cart-add-toast";
import { getShopifyCartUrl, getShopifyRoutesRoot } from "../utils/shopify-cart-url";

const storeName = window?.Shopify?.shop || window.location.hostname;

const formatProductsPriceFields = (products = []) => products.map(formatProductPriceFields);

/** Active storefront popup — set while a product popup is open (common revenue tracking). */
let activeStorefrontTrackingContext = null;

export function setActiveStorefrontTrackingContext(context) {
  activeStorefrontTrackingContext = context || null;
}

export function getActiveStorefrontTrackingContext() {
  return activeStorefrontTrackingContext;
}

const addToCart = async (items, options = {}) => {
  const shouldAttach =
    options.attachTracking !== false && activeStorefrontTrackingContext;
  const payloadItems = shouldAttach
    ? attachStorefrontTrackingToCartItems(items, {
        ...activeStorefrontTrackingContext,
        ...options.tracking,
      })
    : items;

  // Only suppress-modal (widget ATC) sets this. Theme Product Add-Ons prepend
  // uses bypassCartFetchPatch + native fetch — must NOT set the flag, or a
  // concurrent theme /cart/add would skip Modal-on-Cart-Add.
  const markInternalCartAdd =
    typeof window !== "undefined" &&
    options.suppressModalOnCartAddRefire === true;
  if (markInternalCartAdd) {
    window.__WEUPSELL_INTERNAL_CART_ADD__ = true;
  }
  const bypassCartFetchPatch =
    typeof window !== "undefined" && options.bypassCartFetchPatch === true;
  // Prefer native fetch for nested Product Add-Ons prepend so a concurrent theme
  // /cart/add is NOT also bypassed by a global flag (that skipped Modal-on-Cart-Add).
  const nativeFetch =
    bypassCartFetchPatch &&
    typeof window !== "undefined" &&
    typeof window.__WEUPSELL_NATIVE_FETCH__ === "function"
      ? window.__WEUPSELL_NATIVE_FETCH__
      : null;
  const useGlobalBypassFlag = bypassCartFetchPatch && !nativeFetch;
  if (useGlobalBypassFlag) {
    window.__WEUPSELL_BYPASS_CART_FETCH_PATCH__ = true;
  }

  try {
    const fetchFn = nativeFetch || fetch;
    const response = await fetchFn(getShopifyCartUrl("cart/add.js"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: payloadItems,
        sections: resolveCartSectionsToRequest(),
        sections_url:
          typeof window !== "undefined" ? window.location.pathname : "/",
      }),
    });

    if (!response.ok) {
      const message = await parseCartAddErrorFromResponse(response);
      const err = new Error(message);
      err.status = response.status;
      throw err;
    }

    const data = await response.json();

    if (options.syncCartAfterAdd !== false) {
      try {
        await syncStorefrontCartAfterAdd(data);
      } catch (syncError) {
        console.warn("[cart.service] Cart UI sync after add failed:", syncError);
      }
    }

    return data;
  } finally {
    if (useGlobalBypassFlag && typeof window !== "undefined") {
      window.__WEUPSELL_BYPASS_CART_FETCH_PATCH__ = false;
    }
    if (markInternalCartAdd && typeof window !== "undefined") {
      window.__WEUPSELL_INTERNAL_CART_ADD__ = false;
    }
  }
  // } catch (error) {
  //   console.error('Error adding to cart:', error);
  // }
};

const getIp = () => { return axios.get("https://api.ipify.org?format=json"); };

const addToCartGraphql = async (items) => {
  try {
    const responseIp = await getIp();
    const response = await api.post(
      `/api/public/cart`,
      { items, storeName, ip: responseIp?.data?.ip }
    );

    return response?.data;
  } catch (error) {
    console.log("Error in adding account", error);
  }
};

const getCheckoutUrl = async (items, options = {}) => {
  try {

    const responseIp = await getIp();
    const discountCodes = Array.isArray(options.discountCodes)
      ? options.discountCodes.filter(Boolean)
      : options.discountCode
        ? [options.discountCode]
        : [];

    const response = await api.post(
      `/api/public/checkout`,
      {
        items,
        storeName,
        ip: responseIp?.data?.ip,
        ...(options.note != null ? { note: String(options.note) } : {}),
        ...(discountCodes.length ? { discountCodes } : {}),
      }
    );

    return response?.data;
  } catch (error) {
    console.log("Error in adding account", error);
  }
};


const fetchCart = async () => {
  try {
    const res = await fetch(getShopifyCartUrl("cart.js"));
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  } catch (err) {
    console.error("Error fetching cart:", err);
    throw err;
  }
};

const changeQuantity = async (line, quantity) => {
  return changeLineItem({ line, quantity });
};

/** Optional hook for drawer write-queue key cache (no-op when queue absent). */
export function seedCartWriteSnapshot(_cart) {}

/**
 * Swap a cart line to a different variant.
 * Shopify /cart/change.js only updates quantity/properties for an existing
 * line key — it cannot change variant_id. Remove the old line, then add the
 * new variant (preserving qty / properties / selling plan).
 */
const changeLineItemVariant = async ({
  lineKey,
  variantId,
  quantity = 1,
  properties,
  sellingPlanId,
  syncCartUi = true,
}) => {
  const key = lineKey;
  const nextVariantId = variantId;
  if (!key) throw new Error("Cart line key is required");
  if (!nextVariantId) throw new Error("Variant id is required");

  const qty = Math.max(1, Number(quantity) || 1);
  await changeLineItem({
    id: key,
    quantity: 0,
    syncCartUi: false,
  });

  const addItem = {
    id: Number(nextVariantId) || nextVariantId,
    quantity: qty,
  };
  if (properties && typeof properties === "object") {
    const cleaned = Object.fromEntries(
      Object.entries(properties).filter(
        ([, value]) => value != null && String(value) !== ""
      )
    );
    if (Object.keys(cleaned).length) addItem.properties = cleaned;
  }
  if (sellingPlanId) {
    addItem.selling_plan = Number(sellingPlanId) || sellingPlanId;
  }

  await addToCart([addItem], {
    attachTracking: false,
    suppressModalOnCartAddRefire: true,
    syncCartAfterAdd: false,
  });

  const cartSnapshot = await fetchCart();
  if (syncCartUi !== false) {
    try {
      await syncStorefrontCartUi(cartSnapshot, {
        cartSnapshot,
        fetchCart: false,
        refreshDrawer: false,
      });
    } catch (syncError) {
      console.warn(
        "[cart.service] Cart UI sync after variant change failed:",
        syncError
      );
    }
  }
  return cartSnapshot;
};

const changeLineItem = async ({
  line,
  id,
  quantity,
  properties,
  syncCartUi = true,
}) => {
  // Prefer stable line `key`/`id` over 1-based `line` index — reward gift
  // add/remove shifts indexes and races with qty changes otherwise.
  const lineKey = id || line;
  if (!lineKey) throw new Error("Cart line key is required");

  const payload = { id: lineKey };
  if (quantity !== undefined) payload.quantity = quantity;
  if (properties !== undefined) payload.properties = properties;
  payload.sections = resolveCartSectionsToRequest();
  payload.sections_url =
    typeof window !== "undefined" ? window.location.pathname : "/";

  const res = await fetch(getShopifyCartUrl("cart/change.js"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const message = await parseCartAddErrorFromResponse(res);
    throw new Error(message);
  }
  const changed = await res.json();
  // Prefer cart.js — change.js can return incorrect variant images for
  // multiple variants of the same product after remove/qty updates.
  let cartSnapshot = changed;
  try {
    cartSnapshot = await fetchCart();
  } catch {
    cartSnapshot = changed;
  }

  if (syncCartUi !== false) {
    try {
      await syncStorefrontCartUi(changed, {
        cartSnapshot,
        fetchCart: false,
        // Reward sync / drawer qty already updates the static drawer via
        // applyCartSnapshot — avoid a redundant refresh race.
        refreshDrawer: false,
      });
    } catch (syncError) {
      console.warn("[cart.service] Cart UI sync after change failed:", syncError);
    }
  }

  return cartSnapshot;
};

// Apply cart updates (discount code, order note) via Shopify's cart/update.js
const updateCart = async (item = {}) => {
  try {
    const payload = {};
    if (item?.clearDiscount === true) {
      payload.discount = "";
    } else {
      const discount =
        item?.discount ?? item?.discount_details?.discount_code;
      if (discount != null && String(discount).trim() !== "") {
        payload.discount = String(discount).trim();
      }
    }
    if (item?.note != null) {
      payload.note = String(item.note);
    }
    if (
      item?.attributes &&
      typeof item.attributes === "object" &&
      !Array.isArray(item.attributes)
    ) {
      payload.attributes = item.attributes;
    }
    if (!Object.keys(payload).length) return null;

    const res = await fetch(getShopifyCartUrl("cart/update.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error("Failed to update cart");
    return await res.json();
  } catch (err) {
    console.error("Error updating cart:", err);
    throw err;
  }
};

const clearCartDiscount = async () => updateCart({ clearDiscount: true });

const fetchStoreDiscountCodes = async (shopOverride) => {
  const shop = String(
    shopOverride || window?.Shopify?.shop || window.location.hostname || ""
  ).trim();
  if (!shop) return [];
  const query = new URLSearchParams({ shop });
  const result = await api.get(`/api/public/discount-codes?${query.toString()}`);
  return result?.data?.data?.codes || [];
};

// Fetch product info from Shopify frontend by handle
const fetchProductByHandle = async (handle) => {
  if (!handle) throw new Error("Product handle is required");
  
  // Helper function to check if we're in local testing mode
  const isLocalTesting = () => {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  };

  // For local testing, try to use a fallback approach
  if (isLocalTesting()) {
    console.log('[Local Testing] Attempting to fetch from Shopify API in local environment');
    
    // Try to construct the URL using the current page's domain if available
    const currentUrl = window.location.href;
    const urlMatch = currentUrl.match(/https:\/\/([^\/]+)/);
    
    if (urlMatch) {
      const shopDomain = urlMatch[1];
      const url = `https://${shopDomain}/products/${handle}.js`;
      
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch product info for handle: ${handle}`);
        const productData = await response.json();
        
        // Ensure the product data has the correct structure for images
        if (productData && !productData.images && productData.image) {
          // If the product has a single image, convert it to images array format
          productData.images = [productData.image];
        }
        
        return productData;
      } catch (err) {
        console.error("Error fetching product by handle in local testing:", err);
        throw new Error('Cannot fetch from Shopify API in local environment');
      }
    } else {
      throw new Error('Cannot fetch from Shopify API in local environment');
    }
  }

  // Locale-aware same-origin product JSON (avoids primary-language myshopify.com responses).
  const url = `${getShopifyRoutesRoot()}products/${handle}.js`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch product info for handle: ${handle}`);
    const productData = await response.json();

    const formattedProduct = formatProductPriceFields(productData);

    // Ensure the product data has the correct structure for images
    if (formattedProduct && !formattedProduct.images && formattedProduct.image) {
      // If the product has a single image, convert it to images array format
      formattedProduct.images = [formattedProduct.image];
    }
    
    return formattedProduct;
  } catch (err) {
    console.error("Error fetching product by handle:", err);
    throw err;
  }
};

// Fetch recommendation offer products — storefront GraphQL first, server fallback.
const fetchProductRecommendations = async (productId, intent = "related", limit = 1) => {
  if (!productId) throw new Error("Product ID is required");

  const type = intent === "complementary" ? "ai_recommended" : "shopify_recommended";
  const products = await resolveOfferProducts({
    productsToOffer: { type, value: [{ id: productId }] },
    productCount: limit,
    allowMultipleProducts: true,
    contextProduct: { id: productId },
    apiGet: api.get.bind(api),
    caller: "cart-service-recommendations",
  });

  const list = Array.isArray(products) ? products : products ? [products] : [];
  return formatProductsPriceFields(list);
};

const cartService = {
  addToCart,
  setActiveStorefrontTrackingContext,
  getActiveStorefrontTrackingContext,
  // getCheckoutUrl,
  getCheckoutUrl,
  fetchCart,
  changeQuantity,
  changeLineItem,
  changeLineItemVariant,
  fetchProductByHandle,
  fetchProductRecommendations,
  updateCart,
  clearCartDiscount,
  fetchStoreDiscountCodes,
}


export default cartService;

